/** Unlockable cameras and graphics packages (earned through Career). */
import type { CameraKind } from '../core/contracts';

export type UnlockId =
  | 'cam_spidercam' | 'cam_blimp' | 'cam_super_slomo'
  | 'gfx_regional' | 'gfx_national' | 'gfx_continental' | 'gfx_final';

export type GraphicsPackageId = 'gfx_basic' | 'gfx_regional' | 'gfx_national' | 'gfx_continental' | 'gfx_final';

export interface UnlockDef {
  id: UnlockId;
  kind: 'camera' | 'graphics';
  name: string;
  description: string;
  cameraKind?: CameraKind;
  /** Higher rank packages supersede lower ones. */
  rank?: number;
}

export const UNLOCKS: readonly UnlockDef[] = [
  { id: 'gfx_regional', kind: 'graphics', name: 'Regional Graphics', description: 'Animated scorebug and team-colour lower thirds.', rank: 1 },
  { id: 'cam_spidercam', kind: 'camera', name: 'Spidercam', description: 'Cable-suspended camera that flies over the pitch.', cameraKind: 'spidercam' },
  { id: 'gfx_national', kind: 'graphics', name: 'National Graphics', description: 'Broadcast-grade stat panels and possession bar.', rank: 2 },
  { id: 'cam_blimp', kind: 'camera', name: 'Blimp', description: 'Aerial stadium shots for establishing and set pieces.', cameraKind: 'blimp' },
  { id: 'gfx_continental', kind: 'graphics', name: 'Continental Graphics', description: 'Premium glass-and-chrome package with stingers.', rank: 3 },
  { id: 'cam_super_slomo', kind: 'camera', name: 'Super Slo-Mo', description: 'High-frame-rate camera for gorgeous replays.', cameraKind: 'super_slomo' },
  { id: 'gfx_final', kind: 'graphics', name: 'Cup Final Graphics', description: 'Gold-trimmed final package.', rank: 4 },
];

export function unlockDef(id: string): UnlockDef | undefined {
  return UNLOCKS.find((u) => u.id === id);
}

export function isUnlocked(unlocked: readonly string[], id: UnlockId): boolean {
  return unlocked.includes(id);
}

/** Adds ids, returning the new list plus which ones were newly granted. */
export function grantUnlocks(unlocked: readonly string[], ids: readonly UnlockId[]): { unlocked: string[]; granted: UnlockId[] } {
  const next = [...unlocked];
  const granted: UnlockId[] = [];
  for (const id of ids) if (!next.includes(id)) { next.push(id); granted.push(id); }
  return { unlocked: next, granted };
}

export function unlockedCameraKinds(unlocked: readonly string[]): CameraKind[] {
  return UNLOCKS.filter((u) => u.kind === 'camera' && unlocked.includes(u.id)).map((u) => u.cameraKind!);
}

export function bestGraphicsPackage(unlocked: readonly string[]): GraphicsPackageId {
  let best: GraphicsPackageId = 'gfx_basic';
  let rank = 0;
  for (const u of UNLOCKS) {
    if (u.kind === 'graphics' && unlocked.includes(u.id) && (u.rank ?? 0) > rank) { rank = u.rank ?? 0; best = u.id as GraphicsPackageId; }
  }
  return best;
}
